import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import { useClasses, createUseStyles, Text, useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { RichTextContext } from '../../';

import styles from './styles';

const useStyles = createUseStyles(styles, 'EmojiList');

const EmojiList = forwardRef(({ texts, command, classes: classesProp, items = [], query }, ref) => {
    const { saveCache } = useContext(RichTextContext);
    const classes = useClasses(useStyles, classesProp);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const selectedIndexRef = useRef(0);
    const theme = useTheme();
    const { t } = useWeb5Context();

    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    useEffect(() => setSelectedIndex(0), [items]);

    const selectItem = useCallback(
        (index) => {
            const item = items[index];
            if (item) {
                command({ name: item.name });
                saveCache(item.name);
            }
        },
        [command, items, saveCache],
    );

    const upHandler = useCallback(() => {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
    }, [items]);

    const downHandler = useCallback(() => {
        setSelectedIndex((prev) => (prev + 1) % items.length);
    }, [items]);

    const enterHandler = useCallback(() => {
        selectItem(selectedIndexRef.current);
    }, [selectItem]);

    useImperativeHandle(
        ref,
        () => ({
            onKeyDown: ({ event }) => {
                switch (event.key) {
                    case 'ArrowUp':
                        upHandler();
                        return true;
                    case 'ArrowDown':
                        downHandler();
                        return true;
                    case 'Enter':
                    case 'Tab':
                        enterHandler();
                        return true;
                    default:
                        return false;
                }
            },
        }),
        [downHandler, enterHandler, upHandler],
    );

    return query.length >= 3 ? (
        <div className={classes.root}>
            {items.length ? (
                items.map((item, index) => (
                    <div
                        className={classNames(classes.item, {
                            [classes.selected]: index === selectedIndex,
                        })}
                        key={index}
                        onClick={() => selectItem(index)}
                    >
                        <Text
                            color={theme.colors.textLight.primary}
                            type="body"
                            className={classes.emojiItem}
                            isTruncated
                        >
                            <span>{item.emoji}</span>
                            <span>:{item.name}:</span>
                        </Text>
                    </div>
                ))
            ) : (
                <Text>{texts?.noResults || t('label_no_results')}</Text>
            )}
        </div>
    ) : null;
});

export default EmojiList;
